/**
 *  このファイルはtools/pullSchema.tsによりコピーされたファイルです。手動で編集しないでください。
 *  変更を加えるにはotobuki-resv-apiのschemaを編集してコピーしてください。
 */

import { isHoliday } from "japanese-holidays";

type DayOfWeekNumber = 0 | 1 | 2 | 3 | 4 | 5 | 6;
type Format = "YYYY-MM-dd" | "YYYY-MM-dd HH:mm" | "YYYY年MM月dd日" | "YYYY年MM月dd日 HH:mm" | "YYYY年MM月dd日(E) HH:mm" | "YYYY-MM" | "YYYY年MM月" | "MM/dd (E)" | "YYYY-MM-dd (E) HH:mm" | "MM月dd日(E)" | "YYYY-MM-dd (E)" | "MM月dd日" | "MM/dd HH:mm" | "HH:mm";

//コンストラクタでUTCにしてtoUnixで日本時間にしている。
//get,setともにUTCがつく関数を使う。
export default class Time {
  static readonly DayOfWeek = ["日", "月", "火", "水", "木", "金", "土"] as const;
  static readonly TimeDifference = 9 * 60 * 60 * 1000;
  static readonly ADay = 24 * 60 * 60 * 1000;
  static readonly AnHour = 60 * 60 * 1000;
  static readonly AMinute = 60 * 1000;
  static readonly ASecond = 1000;
  private readonly utcDate;

  constructor(epochMillis: number) {
    this.utcDate = new Date(epochMillis + Time.TimeDifference);
  }

  public static readonly now = () => {
    return new Time(Date.now());
  };

  private readonly clone = () => {
    return new Time(this.getMillis());
  };

  readonly getMillis = () => {
    return this.utcDate.getTime() - Time.TimeDifference;
  };

  public static readonly fromISO = (isoTimeString: string) => {
    return new Time(new Date(isoTimeString).getTime());
  };

  readonly startOfHour = () => {
    const clone = this.clone();
    clone.utcDate.setUTCMinutes(0, 0, 0);
    return clone;
  };

  readonly startOfDay = () => {
    const clone = this.clone();
    clone.utcDate.setUTCHours(0, 0, 0, 0);
    return clone;
  };

  readonly startOfMonth = () => {
    const clone = this.clone();
    clone.utcDate.setUTCDate(1);
    clone.utcDate.setUTCHours(0, 0, 0, 0);
    return clone;
  };

  readonly startOfYear = () => {
    const clone = this.clone();
    clone.utcDate.setUTCMonth(0, 1);
    clone.utcDate.setUTCHours(0, 0, 0, 0);
    return clone;
  };

  readonly shiftYear = (diff: number) => {
    const clone = this.clone();
    clone.utcDate.setUTCFullYear(clone.utcDate.getUTCFullYear() + diff);
    return clone;
  };

  readonly shiftMonth = (diff: number) => {
    const clone = this.clone();
    clone.utcDate.setUTCMonth(clone.utcDate.getUTCMonth() + diff);
    return clone;
  };

  readonly shiftDate = (diff: number) => {
    const clone = this.clone();
    clone.utcDate.setUTCDate(clone.utcDate.getUTCDate() + diff);
    return clone;
  };

  //その月の日付のリストを返す
  readonly getDatesOfMonth = () => {
    const startOfMonth = this.startOfMonth();
    const datesOfMonth: Time[] = [];
    for (let date = startOfMonth; date.lt(startOfMonth.shiftMonth(1)); date = date.shiftDate(1)) {
      datesOfMonth.push(date);
    }
    return datesOfMonth;
  };

  //月日まで考慮した年齢を計算する
  readonly getAge = () => {
    const now = Time.now();
    const birthValues = this.values();
    const nowValues = now.values();

    let age = nowValues.year - birthValues.year;

    // 今年の誕生日がまだ来ていない場合は1を引く
    if (nowValues.month < birthValues.month || (nowValues.month === birthValues.month && nowValues.dayOfMonth < birthValues.dayOfMonth)) {
      age--;
    }

    return age;
  };

  readonly dateStrings = () => {
    const year = this.utcDate.getUTCFullYear().toString();
    const month = (this.utcDate.getUTCMonth() + 1).toString();
    const month2 = month.padStart(2, "0");
    const dayOfMonth = this.utcDate.getUTCDate().toString();
    const dayOfMonth2 = dayOfMonth.padStart(2, "0");
    const hour = this.utcDate.getUTCHours().toString();
    const hour2 = hour.padStart(2, "0");
    const minute = this.utcDate.getUTCMinutes().toString();
    const minute2 = minute.padStart(2, "0");
    const second = this.utcDate.getUTCSeconds().toString();
    const second2 = second.padStart(2, "0");
    const dayOfWeek = Time.DayOfWeek[this.utcDate.getUTCDay()];
    return { dayOfMonth, dayOfMonth2, dayOfWeek, hour, hour2, minute, minute2, month, month2, second, second2, year };
  };

  //時刻などの値。（number）
  //月は1始まり
  readonly values = () => {
    const year = this.utcDate.getUTCFullYear();
    const month = this.utcDate.getUTCMonth() + 1;
    const dayOfMonth = this.utcDate.getUTCDate();
    const hour = this.utcDate.getUTCHours();
    const minute = this.utcDate.getUTCMinutes();
    const second = this.utcDate.getUTCSeconds();
    const dayOfWeek = this.utcDate.getUTCDay() as DayOfWeekNumber;
    return { dayOfMonth, dayOfWeek, hour, minute, month, second, year };
  };

  readonly format = (format: Format) => {
    const ds = this.dateStrings();
    switch (format) {
      case "YYYY-MM-dd":
        return `${ds.year}-${ds.month2}-${ds.dayOfMonth2}`;
      case "YYYY-MM-dd HH:mm":
        return `${ds.year}-${ds.month2}-${ds.dayOfMonth2} ${ds.hour2}:${ds.minute2}`;
      case "YYYY年MM月dd日":
        return `${ds.year}年${ds.month2}月${ds.dayOfMonth2}日`;
      case "YYYY年MM月dd日 HH:mm":
        return `${ds.year}年${ds.month2}月${ds.dayOfMonth2}日 ${ds.hour2}:${ds.minute2}`;
      case "YYYY年MM月dd日(E) HH:mm":
        return `${ds.year}年${ds.month2}月${ds.dayOfMonth2}日(${ds.dayOfWeek}) ${ds.hour2}:${ds.minute2}`;
      case "YYYY-MM-dd (E) HH:mm":
        return `${ds.year}-${ds.month2}-${ds.dayOfMonth2} (${ds.dayOfWeek}) ${ds.hour2}:${ds.minute2}`;
      case "YYYY-MM":
        return `${ds.year}-${ds.month2}`;
      case "YYYY年MM月":
        return `${ds.year}年${ds.month2}月`;
      case "MM/dd (E)":
        return `${ds.month2}/${ds.dayOfMonth2} (${ds.dayOfWeek})`;
      case "MM月dd日(E)":
        return `${ds.month2}月${ds.dayOfMonth2}日(${ds.dayOfWeek})`;
      case "YYYY-MM-dd (E)":
        return `${ds.year}-${ds.month2}-${ds.dayOfMonth2} (${ds.dayOfWeek})`;
      case "MM月dd日":
        return `${ds.month2}月${ds.dayOfMonth2}日`;
      case "MM/dd HH:mm":
        return `${ds.month2}/${ds.dayOfMonth2} ${ds.hour2}:${ds.minute2}`;
      case "HH:mm":
        return `${ds.hour2}:${ds.minute2}`;
    }
  };

  readonly isHoliday = () => {
    const { year, month, dayOfMonth } = this.values();
    return isHoliday(new Date(year, month - 1, dayOfMonth), true);
  };

  readonly lt = (another: Time) => this.getMillis() < another.getMillis();
  readonly lte = (another: Time) => this.getMillis() <= another.getMillis();
  readonly gt = (another: Time) => this.getMillis() > another.getMillis();
  readonly gte = (another: Time) => this.getMillis() >= another.getMillis();
  readonly eq = (another: Time) => this.getMillis() === another.getMillis();
}
